import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  deleteCrossClientLandingExecution,
  selectCrossClientLandingDecompositionResult,
  selectCrossClientLandingInitialization,
  setCrossClientLandingDecompositionResult,
  setCrossClientLandingInitialization,
  setCrossClientEvaluateLoading,
  setCrossClientInitLoading,
} from '../../../store/crossClient/crossClientLanding'
import {
  useDecomposeCrossClientQueryMutation,
  useEvaluateClauseResolversCrossClientQueryMutation,
  useInitializeCrossClientQueryMutation,
} from '../../../store/crossClient/crossClientExecution'
import { useDeleteCrossClientQueryExecutionMutation } from '../../../store/crossClient/crossClient'
import { selectAccountId } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { CROSS_CLIENT_FUNDS_TOAST_MESSAGE, isCrossClientFundsQuotaError } from '../utils/crossClientFunds'
import {
  type CrossClientQueryAtomicClause,
  type CrossClientQueryClientQueryMode,
  type CrossClientQueryDataSource,
  type CrossClientQueryLogicalQuestionDecomposition,
  type CrossClientQueryLogicalQuestionNNFDecomposition,
  type CrossClientQueryResultSummary,
  type CrossClientResultTab,
  CrossClientResultTabs,
} from '../../../models/CrossClient'

interface DecomposeArgsBase {
  userQuestion: string
  sources: CrossClientQueryDataSource[]
  clientQueryMode: CrossClientQueryClientQueryMode
}

interface CustomDecomposeArgs extends DecomposeArgsBase {
  predefinedQueryId?: never
  decomposition?: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
}

interface PredefinedDecomposeArgs extends DecomposeArgsBase {
  predefinedQueryId: string
  decomposition: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
}

type DecomposeArgs = CustomDecomposeArgs | PredefinedDecomposeArgs

export const MAX_USER_QUESTION_LENGTH = 350

interface DecomposeOptions {
  skipReviewPipeline?: boolean
}

interface ReviewCommonFields {
  atomicClauses: CrossClientQueryAtomicClause[]
  hasLocal: boolean
  sources: CrossClientQueryDataSource[]
}

export type CrossClientReviewState =
  | { kind: 'preview'; atomicClauses: CrossClientQueryAtomicClause[]; sources: CrossClientQueryDataSource[] }
  | { kind: 'initializing'; atomicClauses: CrossClientQueryAtomicClause[]; sources: CrossClientQueryDataSource[] }
  | ({ kind: 'bulk-running' } & ReviewCommonFields)
  | ({ kind: 'bulk-done'; resultSummary: CrossClientQueryResultSummary } & ReviewCommonFields)
  | ({ kind: 'no-bulk' } & ReviewCommonFields)

const resultTabForReviewState = (state: CrossClientReviewState | null): CrossClientResultTab => {
  if (state?.kind === 'bulk-done') {
    if (state.resultSummary.fullCount > 0) {
      return CrossClientResultTabs.MATCH
    }
    if (state.resultSummary.potentialCount > 0) {
      return CrossClientResultTabs.POTENTIAL
    }
  }
  return CrossClientResultTabs.REMAINING
}

export const useCrossClientQueryLandingExecution = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const decompositionResult = useAppSelector(selectCrossClientLandingDecompositionResult)
  const initialization = useAppSelector(selectCrossClientLandingInitialization)

  const [triggerDecomposeCrossClientQuery] = useDecomposeCrossClientQueryMutation()
  const [triggerInitializeCrossClientQuery] = useInitializeCrossClientQueryMutation()
  const [triggerEvaluateClauseResolvers] = useEvaluateClauseResolversCrossClientQueryMutation()
  const [triggerDeleteCrossClientExecution] = useDeleteCrossClientQueryExecutionMutation()

  const [reviewState, setReviewState] = useState<CrossClientReviewState | null>(null)

  const openResults = useCallback(
    (executionId: string, tab: CrossClientResultTab) => {
      navigate(`/cross-client/executions/${executionId}?tab=${tab}`, { replace: true })
    },
    [navigate],
  )

  const decomposeCrossClientQuery = useCallback(
    async (args: DecomposeArgs, options?: DecomposeOptions) => {
      let decomposition = args.decomposition
      let nnfDecomposition = args.nnfDecomposition
      try {
        dispatch(setCrossClientLandingDecompositionResult(null))
        dispatch(setCrossClientLandingInitialization(null))
        setReviewState(null)
        dispatch(setCrossClientInitLoading(true))
        if (!decomposition) {
          const decomposeResult = await triggerDecomposeCrossClientQuery({
            accountId,
            payload: { userQuestion: args.userQuestion },
          }).unwrap()
          decomposition = decomposeResult.decomposition
          nnfDecomposition = decomposeResult.nnfDecomposition
        }
        dispatch(setCrossClientLandingDecompositionResult({ decomposition, nnfDecomposition }))
      } catch (e) {
        if (isCrossClientFundsQuotaError(e)) {
          dispatch(addToast({ variant: 'error', title: 'Not enough credits', messages: [CROSS_CLIENT_FUNDS_TOAST_MESSAGE] }))
        } else {
          console.error(e)
        }
        return
      } finally {
        dispatch(setCrossClientInitLoading(false))
      }

      const atomicClauses = decomposition.atomicClauses ?? []
      if (atomicClauses.length === 0) {
        return
      }

      let initialization
      try {
        setReviewState({ kind: 'initializing', atomicClauses, sources: args.sources })
        initialization = await triggerInitializeCrossClientQuery({
          accountId,
          payload: args.predefinedQueryId
            ? {
                predefinedQueryId: args.predefinedQueryId,
                sources: args.sources,
                clientQueryMode: args.clientQueryMode,
              }
            : {
                userQuestion: args.userQuestion,
                decomposition,
                sources: args.sources,
                clientQueryMode: args.clientQueryMode,
                nnfDecomposition,
              },
        }).unwrap()
        dispatch(setCrossClientLandingInitialization(initialization))
      } catch (e) {
        console.error(e)
        dispatch(setCrossClientLandingDecompositionResult(null))
        dispatch(setCrossClientLandingInitialization(null))
        setReviewState(null)
        return
      }

      if (options?.skipReviewPipeline) {
        return
      }

      const hasBulk = initialization.resolvableClauseVariables.length > 0
      const hasLocal = initialization.localResolvableClauseVariables.length > 0
      const common: ReviewCommonFields = { atomicClauses, hasLocal, sources: args.sources }

      if (!hasBulk) {
        setReviewState({ kind: 'no-bulk', ...common })
        return
      }

      setReviewState({ kind: 'bulk-running', ...common })
      try {
        const resultSummary = await triggerEvaluateClauseResolvers({ accountId, executionId: initialization.executionId }).unwrap()
        setReviewState({ kind: 'bulk-done', resultSummary, ...common })
      } catch (e) {
        if (isCrossClientFundsQuotaError(e)) {
          dispatch(addToast({ variant: 'error', title: 'Not enough credits', messages: [CROSS_CLIENT_FUNDS_TOAST_MESSAGE] }))
        } else {
          console.error(e)
        }
        setReviewState({ kind: 'no-bulk', ...common })
      }
    },
    [dispatch, accountId, triggerDecomposeCrossClientQuery, triggerInitializeCrossClientQuery, triggerEvaluateClauseResolvers],
  )

  const initError = useMemo(() => {
    if (!decompositionResult) {
      return null
    }
    if (!decompositionResult.decomposition?.atomicClauses?.length) {
      return 'Unable to decompose question'
    }
    return null
  }, [decompositionResult])

  const previewPredefinedQuery = useCallback(
    (decomposition: CrossClientQueryLogicalQuestionDecomposition, sources: CrossClientQueryDataSource[]) => {
      dispatch(setCrossClientLandingDecompositionResult(null))
      dispatch(setCrossClientLandingInitialization(null))
      setReviewState({ kind: 'preview', atomicClauses: decomposition.atomicClauses ?? [], sources })
    },
    [dispatch],
  )

  const resetExecution = useCallback(() => {
    dispatch(setCrossClientLandingDecompositionResult(null))
    dispatch(setCrossClientLandingInitialization(null))
    setReviewState(null)
  }, [dispatch])

  const discardExecution = useCallback(async () => {
    const executionId = initialization?.executionId
    if (executionId) {
      try {
        await triggerDeleteCrossClientExecution({ accountId, executionId }).unwrap()
        dispatch(deleteCrossClientLandingExecution(executionId))
      } catch (e) {
        console.error(e)
      }
    }
    resetExecution()
  }, [accountId, initialization?.executionId, triggerDeleteCrossClientExecution, dispatch, resetExecution])

  const finalizeExecution = useCallback(
    async (executionId: string) => {
      dispatch(setCrossClientEvaluateLoading(true))
      openResults(executionId, resultTabForReviewState(reviewState))
    },
    [dispatch, openResults, reviewState],
  )

  return {
    initError,
    reviewState,
    decomposeCrossClientQuery,
    previewPredefinedQuery,
    finalizeExecution,
    resetExecution,
    discardExecution,
  }
}
